/* ════════════════════════════════════════════════════════════════════
   NDS COLLECTION MODAL — Client v2.0
   ════════════════════════════════════════════════════════════════════
   Single-shell architecture:
     • One `.nds-cm` shell exists in the DOM.
     • Each collection_modal Liquid block renders its SSR cards + settings
       into a hidden <template data-nds-cm-source="..."> element.
     • Opening a collection hydrates the shell from the matching template.
     • Switching collections (from an already-open shell) simply re-
       hydrates the shell's grid + toolbar — the shell, backdrop, dialog
       frame, and site header stay absolutely still.
   Benefits:
     • No swap cross-fades, no modal-to-modal choreography.
     • No HeaderGuard / HeaderFreezer defense layers needed — the header
       never changes state during collection switching.
     • Client-side filters + sort + load-more work against the shell's
       live grid (single source of truth for the active collection).
   ════════════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  // ── CONSTANTS ────────────────────────────────────────────────────
  var BODY_LOCK_CLASS = 'nds-cm-open';
  var URL_PARAM = 'cm';
  var SWAP_FADE_MS = 140;      // duration of fade-OUT before swapping DOM
  var SWAP_STAGGER_MS = 520;   // how long the post-swap card cascade runs

  var M = (typeof window !== 'undefined' && window.Motion) ? window.Motion : null;
  var prefersReduced = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  var animEnabled = !!M && !prefersReduced;
  var SPRING_SHELL_CM = { stiffness: 260, damping: 30, mass: 1 };
  var SPRING_RAIL_CM  = { stiffness: 300, damping: 34, mass: 1 };
  var EASE_EXIT_CM = [0.32, 0, 0.67, 0];
  var animsCm = { shell: null };

  var SORT_OPTIONS = [
    { value: 'manual',              label: 'Featured' },
    { value: 'best-selling',        label: 'Best selling' },
    { value: 'title-ascending',     label: 'Title A-Z' },
    { value: 'title-descending',    label: 'Title Z-A' },
    { value: 'price-ascending',     label: 'Price low to high' },
    { value: 'price-descending',    label: 'Price high to low' },
    { value: 'created-descending',  label: 'Newest' },
    { value: 'created-ascending',   label: 'Oldest' }
  ];

  var SORT_COMPARATORS = {
    'manual':              function (a, b) { return a.manualIndex - b.manualIndex; },
    'best-selling':        function (a, b) { return a.manualIndex - b.manualIndex; },
    'title-ascending':     function (a, b) { return a.title.localeCompare(b.title); },
    'title-descending':    function (a, b) { return b.title.localeCompare(a.title); },
    'price-ascending':     function (a, b) { return a.price - b.price; },
    'price-descending':    function (a, b) { return b.price - a.price; },
    'created-descending':  function (a, b) { return b.createdAt - a.createdAt; },
    'created-ascending':   function (a, b) { return a.createdAt - b.createdAt; }
  };

  var ICONS = {
    check:      '<svg viewBox="0 0 24 24" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>',
    plus:       '<svg viewBox="0 0 24 24" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>',
    chevron:    '<svg viewBox="0 0 24 24" aria-hidden="true"><polyline points="6 9 12 15 18 9"/></svg>',
    arrowRight: '<svg viewBox="0 0 24 24" aria-hidden="true"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>'
  };

  // ── UTILS ────────────────────────────────────────────────────────
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;' }[c];
    });
  }

  function cssEscape(s) {
    if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(s);
    return String(s || '').replace(/[^a-zA-Z0-9_-]/g, '\\$&');
  }

  function isMobileView() {
    return !!(window.matchMedia && window.matchMedia('(max-width: 749px)').matches);
  }

  function stopAnimCm(key) {
    var a = animsCm[key];
    if (!a) return;
    try { if (typeof a.stop === 'function') a.stop(); } catch (e) {}
    animsCm[key] = null;
  }

  function stopAllAnimsCm() {
    Object.keys(animsCm).forEach(stopAnimCm);
  }

  function animateShellOpen(opts) {
    if (!animEnabled || !shell) return null;
    opts = opts || {};
    stopAnimCm('shell');
    shell.style.willChange = 'transform, opacity';
    var frames = opts.rail
      ? { opacity: [null, 1], transform: [null, 'translateX(0%)'] }
      : { opacity: [null, 1], transform: [null, 'translateY(0px) scale(1)'] };
    var spring = opts.rail ? SPRING_RAIL_CM : SPRING_SHELL_CM;
    var a = M.animate(shell, frames, { easing: M.spring(spring) });
    animsCm.shell = a;
    a.finished.then(function () {
      if (animsCm.shell === a) animsCm.shell = null;
      if (opts.rail) {
        shell.style.opacity = '1';
        shell.style.transform = 'translateX(0%)';
      } else {
        shell.style.opacity = '';
        shell.style.transform = '';
        shell.style.willChange = '';
      }
    }).catch(function () {});
    return a;
  }

  function animateShellClose(opts) {
    if (!animEnabled || !shell) return Promise.resolve();
    opts = opts || {};
    stopAnimCm('shell');
    shell.style.willChange = 'transform, opacity';
    var frames = opts.rail
      ? { opacity: [null, 1], transform: [null, 'translateX(100%)'] }
      : { opacity: [null, 0], transform: [null, 'translateY(6px) scale(0.99)'] };
    var timing = opts.rail
      ? { easing: M.spring(SPRING_RAIL_CM) }
      : { duration: 0.22, easing: EASE_EXIT_CM };
    var a = M.animate(shell, frames, timing);
    animsCm.shell = a;
    return a.finished.catch(function () {}).then(function () {
      if (animsCm.shell === a) animsCm.shell = null;
      if (opts.rail) {
        shell.style.opacity = '1';
        shell.style.transform = 'translateX(100%)';
      }
    });
  }

  // Store's money format injected from Liquid via shell's data-money-format
  // attribute at init(). Empty string until init runs.
  var moneyFormat = '';

  // Shopify-compatible money formatter. Supports all seven placeholders
  // documented at https://shopify.dev/docs/api/liquid/filters/money:
  //   {{amount}}                                   7,890.00
  //   {{amount_no_decimals}}                       7,890
  //   {{amount_with_comma_separator}}              7.890,00
  //   {{amount_no_decimals_with_comma_separator}}  7.890
  //   {{amount_with_space_separator}}              7 890,00
  //   {{amount_no_decimals_with_space_separator}}  7 890
  //   {{amount_with_apostrophe_separator}}         7'890.00
  function ndsFormatMoney(cents, format) {
    var abs = Math.abs(cents);
    var negative = cents < 0 ? '-' : '';
    function thousands(str, sep) {
      return str.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
    }
    function render(hasDec, thousandsSep, decSep) {
      var whole = Math.floor(abs / 100).toString();
      var frac = (abs % 100).toString();
      while (frac.length < 2) frac = '0' + frac;
      whole = thousands(whole, thousandsSep);
      return hasDec ? (whole + decSep + frac) : whole;
    }
    var out = format.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, token) {
      switch (token) {
        case 'amount':                                  return render(true,  ',', '.');
        case 'amount_no_decimals':                      return render(false, ',', '.');
        case 'amount_with_comma_separator':             return render(true,  '.', ',');
        case 'amount_no_decimals_with_comma_separator': return render(false, '.', ',');
        case 'amount_with_space_separator':             return render(true,  ' ', ',');
        case 'amount_no_decimals_with_space_separator': return render(false, ' ', ',');
        case 'amount_with_apostrophe_separator':        return render(true,  "'", '.');
        default: return '';
      }
    });
    return negative + out;
  }

  function formatMoney(cents) {
    if (cents == null) cents = 0;
    var shop = window.Shopify;
    // 1. Native Shopify.formatMoney — ideal; handles all locale quirks.
    if (shop && typeof shop.formatMoney === 'function' && shop.money_format) {
      try { return shop.formatMoney(cents, shop.money_format); } catch (e) {}
    }
    // 2. Shopify.money_format without the formatter — run it through our
    //    ndsFormatMoney. This covers themes that populate the global but
    //    don't load Shopify's money.js helper.
    if (shop && shop.money_format) {
      try { return ndsFormatMoney(cents, shop.money_format); } catch (e) {}
    }
    // 3. Shell-injected money_format (what we pulled from the Liquid attr).
    if (moneyFormat) {
      try { return ndsFormatMoney(cents, moneyFormat); } catch (e) {}
    }
    // 4. Last-resort $-style fallback. Should only be reached on truly
    //    misconfigured stores with no money_format anywhere.
    var sign = cents < 0 ? '-' : '';
    var abs = Math.abs(cents);
    var whole = Math.floor(abs / 100);
    var frac = (abs % 100).toString();
    while (frac.length < 2) frac = '0' + frac;
    return sign + '$' + whole + '.' + frac;
  }

  // Convert a raw variant price (as returned by Shopify's /products/{h}.js)
  // into integer cents. The Ajax API contract (shopify.dev/docs/api/ajax
  // /reference/product) documents `price` as integer cents — e.g. 790 for
  // 7.90 of the shop's base currency. We therefore always parse as integer
  // and DISCARD any trailing decimal portion:
  //    790       → 790
  //   "790"      → 790
  //   "790.00"   → 790   (decimal-serialized cents; parseInt stops at '.')
  //    790.0     → 790   (integer-valued float; rounded)
  // Previous versions branched on "has decimal point → dollars → ×100",
  // which flipped "790.00" into 79,000 cents and displayed $790.00 instead
  // of $7.90 on stores whose Ajax response serializes cents with trailing
  // zeros. Trusting the API contract is the production-safe fix.
  function variantPriceCents(raw) {
    if (raw == null || raw === '') return 0;
    if (typeof raw === 'number') {
      if (!isFinite(raw)) return 0;
      return Math.round(raw);
    }
    var n = parseInt(String(raw), 10);
    return isNaN(n) ? 0 : n;
  }

  // ── SHELL DOM REFS (populated in init) ───────────────────────────
  var shell, dialog, title, countNum, countLabel, grid, loadMoreWrap, loadMoreBtn,
      toolbarLeft, toolbarRight, availMenu, priceMenu, sortMenu, sortList,
      availCheckbox, priceMinInput, priceMaxInput,
      bodyEl, backdropEl, closeBtn, backBtn, vpPanel;

  // ── SOURCES REGISTRY ─────────────────────────────────────────────
  var sourceById = Object.create(null);
  var sourceIdByHandle = Object.create(null);

  function collectSources() {
    sourceById = Object.create(null);
    sourceIdByHandle = Object.create(null);
    var tpls = document.querySelectorAll('template[data-nds-cm-source]');
    for (var i = 0; i < tpls.length; i++) {
      var tpl = tpls[i];
      var id = tpl.getAttribute('data-nds-cm-source');
      if (!id) continue;
      sourceById[id] = tpl;
      var h = tpl.getAttribute('data-handle');
      if (h) sourceIdByHandle[h] = id;
    }
  }

  function findSource(idOrHandle) {
    if (!idOrHandle) return null;
    if (sourceById[idOrHandle]) return sourceById[idOrHandle];
    var id = sourceIdByHandle[idOrHandle];
    return id ? sourceById[id] : null;
  }

  function readMeta(source) {
    return {
      id: source.getAttribute('data-nds-cm-source'),
      handle: source.getAttribute('data-handle') || '',
      title: source.getAttribute('data-title') || '',
      count: parseInt(source.getAttribute('data-count'), 10) || 0,
      cols: source.getAttribute('data-cols') || '4',
      colsMobile: source.getAttribute('data-cols-mobile') || '2',
      ratio: source.getAttribute('data-ratio') || '3/4',
      gap: parseInt(source.getAttribute('data-gap'), 10) || 14,
      initialProducts: parseInt(source.getAttribute('data-initial-products'), 10) || 48,
      defaultSort: source.getAttribute('data-default-sort') || 'manual',
      quickAddOptionsLabel: source.getAttribute('data-quickadd-options-label') || 'Options',
      quickAddAddLabel: source.getAttribute('data-quickadd-add-label') || 'Add',
      showAvailability: source.getAttribute('data-show-availability') === 'true',
      showPrice: source.getAttribute('data-show-price') === 'true',
      showSort: source.getAttribute('data-show-sort') === 'true'
    };
  }

  // ── ACTIVE STATE ─────────────────────────────────────────────────
  var active = {
    id: null,
    handle: null,
    meta: null,
    cardData: [],
    state: null,                    // { sort, availability, priceMin, priceMax, loadedPages }
    productCache: Object.create(null), // per-session variant-picker product JSON cache
    entrySource: null
  };

  var submenuOpen = null;
  var swapStaggerTimer = null; // tracks the stagger-cleanup timeout during a content swap
  var variantPicker = {
    open: false,
    handle: null,
    product: null,
    selected: {},
    triggerBtn: null
  };

  // ── BODY SCROLL LOCK ─────────────────────────────────────────────
  var lockState = {
    locked: false,
    scrollY: 0,
    padR: '',
    bodyOverflow: '',
    bodyTransform: '',
    htmlTransform: ''
  };

  function lockBody() {
    if (lockState.locked) return;
    lockState.scrollY = window.scrollY || 0;
    var sw = window.innerWidth - document.documentElement.clientWidth;
    lockState.padR = document.documentElement.style.paddingRight;
    if (sw > 0) document.documentElement.style.paddingRight = sw + 'px';
    lockState.bodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    if (window.__ndsLenis && window.__ndsLenis.stop) {
      try { window.__ndsLenis.stop(); } catch (e) {}
    }
    lockState.bodyTransform = document.body.style.transform;
    lockState.htmlTransform = document.documentElement.style.transform;
    document.body.style.transform = 'none';
    document.documentElement.style.transform = 'none';
    document.body.classList.add(BODY_LOCK_CLASS);
    document.documentElement.classList.add(BODY_LOCK_CLASS);
    lockState.locked = true;
  }

  function unlockBody() {
    if (!lockState.locked) return;
    document.body.classList.remove(BODY_LOCK_CLASS);
    document.documentElement.classList.remove(BODY_LOCK_CLASS);
    document.documentElement.style.paddingRight = lockState.padR;
    document.body.style.transform = lockState.bodyTransform;
    document.documentElement.style.transform = lockState.htmlTransform;
    document.body.style.overflow = lockState.bodyOverflow;
    if (window.__ndsLenis && window.__ndsLenis.start) {
      try { window.__ndsLenis.start(); } catch (e) {}
    }
    lockState.locked = false;
  }

  // ── HEADER OVERLAYS (mega-menu force-hide while modal is open) ──
  var hiddenMegas = [];
  function hideHeaderOverlays() {
    var megas = document.querySelectorAll('.nds-header__mega');
    for (var i = 0; i < megas.length; i++) {
      var m = megas[i];
      if (!m.classList.contains('nds-header__mega--force-hide')) {
        m.classList.add('nds-header__mega--force-hide');
        hiddenMegas.push(m);
      }
    }
    var subs = document.querySelectorAll('.nds-header__mega-sub[data-open="true"]');
    for (var j = 0; j < subs.length; j++) subs[j].setAttribute('data-open', 'false');
    // Blur any focused element inside the header so its hover/focus states drop.
    var a = document.activeElement;
    if (a && a !== document.body && typeof a.blur === 'function' &&
        a.closest && a.closest('.nds-header-wrapper')) {
      try { a.blur(); } catch (e) {}
    }
  }
  function restoreHeaderOverlays() {
    for (var i = 0; i < hiddenMegas.length; i++) {
      hiddenMegas[i].classList.remove('nds-header__mega--force-hide');
    }
    hiddenMegas = [];
  }

  // ── PAGE INERT ───────────────────────────────────────────────────
  var inertLocked = [];
  function lockPageInert() {
    if (!('inert' in HTMLElement.prototype)) return;
    var targets = document.querySelectorAll('main, #MainContent, [role="main"], #main-content, footer');
    var seen = [];
    for (var i = 0; i < targets.length; i++) {
      var el = targets[i];
      if (seen.indexOf(el) !== -1) continue;
      seen.push(el);
      if (el.contains(shell)) continue;
      if (el.querySelector('.nds-header-wrapper, .nds-cart-drawer, .nds-cm')) continue;
      if (!el.inert) { el.inert = true; inertLocked.push(el); }
    }
  }
  function unlockPageInert() {
    for (var i = 0; i < inertLocked.length; i++) inertLocked[i].inert = false;
    inertLocked = [];
  }

  // ── URL SYNC ─────────────────────────────────────────────────────
  function urlCurrent() {
    try { return new URLSearchParams(window.location.search).get(URL_PARAM); }
    catch (e) { return null; }
  }
  function urlSet(value, replace) {
    try {
      var u = new URL(window.location.href);
      if (value) {
        if (u.searchParams.get(URL_PARAM) === value) return;
        u.searchParams.set(URL_PARAM, value);
      } else {
        if (!u.searchParams.has(URL_PARAM)) return;
        u.searchParams.delete(URL_PARAM);
      }
      var s = { ndsCm: value || null };
      if (replace) window.history.replaceState(s, '', u.toString());
      else window.history.pushState(s, '', u.toString());
    } catch (e) {}
  }

  // ── PREFETCH (collection page 2+ for load-more speed) ───────────
  var prefetchCache = Object.create(null);
  var prefetchInflight = Object.create(null);

  function prefetchPage(handle, page) {
    if (!handle) return Promise.reject(new Error('no-handle'));
    var key = handle + '::' + page;
    if (prefetchCache[key]) return Promise.resolve(prefetchCache[key]);
    if (prefetchInflight[key]) return prefetchInflight[key];
    var url = '/collections/' + encodeURIComponent(handle) + '/products.json?limit=50&page=' + page;
    var p = fetch(url, { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then(function (j) {
        var products = (j && j.products) || [];
        prefetchCache[key] = products;
        delete prefetchInflight[key];
        return products;
      })
      .catch(function (err) { delete prefetchInflight[key]; throw err; });
    prefetchInflight[key] = p;
    return p;
  }
  function prefetchSilent(handle, page) {
    try { prefetchPage(handle, page).catch(function () {}); } catch (e) {}
  }

  // ── HYDRATE CARDS FROM GRID DOM ──────────────────────────────────
  function hydrateCards() {
    active.cardData = [];
    var cards = grid.querySelectorAll('[data-cm-card]');
    for (var i = 0; i < cards.length; i++) {
      var c = cards[i];
      active.cardData.push({
        el: c,
        available: c.dataset.available === 'true',
        price: parseInt(c.dataset.price, 10) || 0,
        compareAtPrice: parseInt(c.dataset.compareAtPrice, 10) || 0,
        createdAt: parseInt(c.dataset.created, 10) || 0,
        title: (c.dataset.title || '').toLowerCase(),
        manualIndex: parseInt(c.dataset.manualIndex, 10) || 0
      });
    }
  }

  // ── TOOLBAR PILL BUILDER ─────────────────────────────────────────
  function buildPill(key, label, withBadge, extraLabel) {
    var html = '<button type="button" class="nds-cm__pill" data-cm-pill="' + esc(key) +
      '" aria-expanded="false" aria-controls="nds-cm-submenu-' + esc(key) + '">' +
      '<span>' + esc(label) + '</span>' +
      (extraLabel || '') +
      ICONS.chevron +
      (withBadge ? '<span class="nds-cm__pill-count" data-cm-pill-badge hidden>1</span>' : '') +
      '</button>';
    return html;
  }

  function sortLabelFor(value) {
    for (var i = 0; i < SORT_OPTIONS.length; i++) {
      if (SORT_OPTIONS[i].value === value) return SORT_OPTIONS[i].label;
    }
    return 'Featured';
  }

  // ── APPLY SOURCE → SHELL (hydrate everything from template data) ──
  function applySource(source) {
    var meta = readMeta(source);
    active.id = meta.id;
    active.handle = meta.handle;
    active.meta = meta;
    active.state = {
      sort: meta.defaultSort,
      availability: false,
      priceMin: null,
      priceMax: null,
      loadedPages: 1
    };

    // Dialog CSS vars (columns, ratio, gap)
    dialog.style.setProperty('--nds-cm-cols', meta.cols);
    dialog.style.setProperty('--nds-cm-cols-mobile', meta.colsMobile);
    dialog.style.setProperty('--nds-cm-ratio', meta.ratio);
    dialog.style.setProperty('--nds-cm-gap', meta.gap + 'px');

    // Title + initial count
    title.textContent = meta.title;
    countNum.textContent = meta.count;
    countLabel.textContent = meta.count === 1 ? 'item' : 'items';

    // Left pills (availability, price)
    var leftHTML = '';
    if (meta.showAvailability) leftHTML += buildPill('availability', 'Availability', true);
    if (meta.showPrice)        leftHTML += buildPill('price', 'Price', true);
    toolbarLeft.innerHTML = leftHTML;

    // Right: view-all link + sort pill
    var rightHTML = '';
    if (meta.count > meta.initialProducts) {
      rightHTML += '<a class="nds-cm__view-all" data-cm-view-all ' +
        'href="/collections/' + esc(meta.handle) + '" ' +
        'title="Open the full collection page with these filters applied">' +
        'View all ' + meta.count + ' ' + ICONS.arrowRight + '</a>';
    }
    if (meta.showSort) {
      var sortLbl = sortLabelFor(meta.defaultSort);
      rightHTML += buildPill('sort', 'Sort:', false,
        '<span data-cm-sort-label>' + esc(sortLbl) + '</span>');
    }
    toolbarRight.innerHTML = rightHTML;

    // Filter submenu visibility (static shells, toggled by data-show-*)
    availMenu.hidden = !meta.showAvailability;
    priceMenu.hidden = !meta.showPrice;
    sortMenu.hidden  = !meta.showSort;

    // Sort submenu options
    if (meta.showSort) {
      var sortHTML = '';
      for (var i = 0; i < SORT_OPTIONS.length; i++) {
        var o = SORT_OPTIONS[i];
        sortHTML += '<li><button type="button" class="nds-cm__submenu-option" ' +
          'role="radio" data-cm-sort="' + esc(o.value) + '" ' +
          'aria-checked="' + (o.value === meta.defaultSort ? 'true' : 'false') + '">' +
          '<span class="nds-cm__submenu-option-label">' + esc(o.label) + '</span>' +
          '</button></li>';
      }
      sortList.innerHTML = sortHTML;
    }

    // Reset filter inputs
    if (availCheckbox) availCheckbox.checked = false;
    if (priceMinInput) priceMinInput.value = '';
    if (priceMaxInput) priceMaxInput.value = '';
    var badges = shell.querySelectorAll('[data-cm-pill-badge]');
    for (var b = 0; b < badges.length; b++) badges[b].hidden = true;

    // Grid — clone from template
    grid.innerHTML = '';
    var clone = source.content.cloneNode(true);
    grid.appendChild(clone);

    // Load-more visibility
    loadMoreWrap.hidden = meta.count <= meta.initialProducts;
    loadMoreBtn.removeAttribute('data-loading');

    // Hydrate + apply default filter/sort state
    hydrateCards();
    applyFiltersAndSort();

    // Scroll body to top (always start from top on open or swap)
    if (bodyEl) bodyEl.scrollTop = 0;
  }

  // ── APPLY FILTERS + SORT AGAINST active.cardData ─────────────────
  function applyFiltersAndSort() {
    var s = active.state;
    if (!s || !active.cardData) return;

    var matched = [];
    for (var i = 0; i < active.cardData.length; i++) {
      var c = active.cardData[i];
      if (s.availability && !c.available) continue;
      if (s.priceMin != null && c.price < s.priceMin) continue;
      if (s.priceMax != null && c.price > s.priceMax) continue;
      matched.push(c);
    }

    var cmp = SORT_COMPARATORS[s.sort] || SORT_COMPARATORS.manual;
    matched.sort(cmp);

    // Hide all, then re-append matched (keeps DOM reflow minimal)
    for (var j = 0; j < active.cardData.length; j++) active.cardData[j].el.hidden = true;
    var frag = document.createDocumentFragment();
    for (var k = 0; k < matched.length; k++) {
      matched[k].el.hidden = false;
      frag.appendChild(matched[k].el);
    }
    grid.appendChild(frag);

    countNum.textContent = matched.length;
    countLabel.textContent = matched.length === 1 ? 'item' : 'items';
    updateViewAllHref();
  }

  function updateViewAllHref() {
    var link = shell.querySelector('[data-cm-view-all]');
    if (!link || !active.handle) return;
    var qs = [];
    var s = active.state;
    if (s.availability)     qs.push('filter.v.availability=1');
    if (s.priceMin != null) qs.push('filter.v.price.gte=' + Math.floor(s.priceMin / 100));
    if (s.priceMax != null) qs.push('filter.v.price.lte=' + Math.ceil(s.priceMax / 100));
    if (s.sort && s.sort !== 'manual') qs.push('sort_by=' + encodeURIComponent(s.sort));
    link.href = '/collections/' + active.handle + (qs.length ? '?' + qs.join('&') : '');
  }

  function updatePillBadges() {
    var availBadge = shell.querySelector('[data-cm-pill="availability"] [data-cm-pill-badge]');
    if (availBadge) availBadge.hidden = !active.state.availability;
    var priceBadge = shell.querySelector('[data-cm-pill="price"] [data-cm-pill-badge]');
    if (priceBadge) priceBadge.hidden = active.state.priceMin == null && active.state.priceMax == null;
  }

  // ── SUBMENU OPEN / CLOSE ─────────────────────────────────────────
  function toggleSubmenu(key) {
    if (submenuOpen === key) { closeSubmenu(); return; }
    closeSubmenu();
    var menu = shell.querySelector('[data-cm-submenu="' + key + '"]');
    var pill = shell.querySelector('[data-cm-pill="' + key + '"]');
    if (!menu || !pill) return;
    submenuOpen = key;
    pill.setAttribute('aria-expanded', 'true');
    menu.setAttribute('data-open', 'true');
    positionSubmenu(menu, pill);
  }

  function closeSubmenu() {
    if (!submenuOpen) return;
    var pill = shell.querySelector('[data-cm-pill="' + submenuOpen + '"]');
    var menu = shell.querySelector('[data-cm-submenu="' + submenuOpen + '"]');
    if (pill) pill.setAttribute('aria-expanded', 'false');
    if (menu) menu.setAttribute('data-open', 'false');
    submenuOpen = null;
  }

  function positionSubmenu(menu, pill) {
    // Submenus live inside .nds-cm__body; position them in body's content
    // coord-space so they scroll with the pill if the body scrolls.
    var anchor = bodyEl || dialog;
    if (!anchor) return;
    var pillRect = pill.getBoundingClientRect();
    var anchorRect = anchor.getBoundingClientRect();
    var gap = 6;
    var edge = isMobileView() ? 8 : 12;
    var menuWidth = menu.offsetWidth || menu.getBoundingClientRect().width || 260;
    var anchorWidth = anchor.clientWidth || anchorRect.width;
    var top = pillRect.bottom - anchorRect.top + anchor.scrollTop + gap;
    var left = pillRect.left - anchorRect.left + anchor.scrollLeft;
    var minLeft = anchor.scrollLeft + edge;
    var maxLeft = anchor.scrollLeft + Math.max(edge, anchorWidth - menuWidth - edge);
    if (left < minLeft) left = minLeft;
    if (left > maxLeft) left = maxLeft;

    menu.style.top = top + 'px';
    menu.style.left = left + 'px';
    menu.style.right = 'auto';
    menu.style.bottom = 'auto';
  }

  // ── SKELETON CARDS (load-more shimmer placeholders) ──────────────
  // Pure visual feedback while the next page is fetched. Cards are
  // appended to the grid end; removed before real cards are inserted.
  function buildSkeletonCard() {
    var art = document.createElement('article');
    art.className = 'nds-cm__card nds-cm__card--skeleton';
    art.setAttribute('data-cm-skeleton', '');
    art.setAttribute('aria-hidden', 'true');
    art.innerHTML =
      '<div class="nds-cm__card-img-wrap nds-cm__skel-img"></div>' +
      '<div class="nds-cm__card-info">' +
        '<div class="nds-cm__skel-line nds-cm__skel-line--title"></div>' +
        '<div class="nds-cm__skel-line nds-cm__skel-line--price"></div>' +
      '</div>';
    return art;
  }

  function showSkeletonCards(count) {
    if (!grid) return;
    var frag = document.createDocumentFragment();
    for (var i = 0; i < count; i++) frag.appendChild(buildSkeletonCard());
    grid.appendChild(frag);
  }

  function removeSkeletonCards() {
    if (!grid) return;
    var skels = grid.querySelectorAll('[data-cm-skeleton]');
    for (var i = 0; i < skels.length; i++) skels[i].parentNode.removeChild(skels[i]);
  }

  // ── LOAD MORE ────────────────────────────────────────────────────
  function loadMore() {
    if (!active.handle) return;
    var handleAtStart = active.handle; // capture — collection may swap during fetch
    var nextPage = active.state.loadedPages + 1;
    loadMoreBtn.setAttribute('data-loading', 'true');

    // Number of skeletons to show matches roughly what the user expects
    // to see: one full row based on the active column count, capped [4..12].
    var cols = parseInt(getComputedStyle(dialog).getPropertyValue('--nds-cm-cols'), 10);
    if (!cols || isNaN(cols)) cols = 4;
    var skelCount = Math.max(4, Math.min(12, cols * 2));
    showSkeletonCards(skelCount);

    prefetchPage(handleAtStart, nextPage)
      .then(function (products) {
        // Guard: user may have switched to a different collection while we
        // were fetching. If so, drop this response — mixing old + new
        // products would break sort, filter, and card-count state.
        if (active.handle !== handleAtStart) { removeSkeletonCards(); return; }
        removeSkeletonCards();
        if (!products || products.length === 0) {
          loadMoreWrap.hidden = true;
          return;
        }
        appendProducts(products);
        active.state.loadedPages = nextPage;
        if (products.length < 50) loadMoreWrap.hidden = true;
        prefetchSilent(handleAtStart, nextPage + 1);
      })
      .catch(function (err) {
        removeSkeletonCards();
        console.warn('[NDS CM] load more failed:', err);
      })
      .then(function () {
        // Only release the loading state if we're still on the same
        // collection — otherwise a new loadMore may already be running
        // for a different source.
        if (active.handle === handleAtStart) loadMoreBtn.removeAttribute('data-loading');
      });
  }

  function appendProducts(products) {
    var startIdx = active.cardData.length;
    var frag = document.createDocumentFragment();
    for (var i = 0; i < products.length; i++) {
      var card = buildCardFromJson(products[i], startIdx + i);
      if (card) frag.appendChild(card);
    }
    grid.appendChild(frag);
    hydrateCards();
    applyFiltersAndSort();
  }

  function buildCardFromJson(p, idx) {
    if (!p || !p.handle) return null;
    var priceCents = p.variants && p.variants.length
      ? variantPriceCents(p.variants[0].price) : 0;
    var compareCents = p.variants && p.variants.length && p.variants[0].compare_at_price
      ? variantPriceCents(p.variants[0].compare_at_price) : 0;
    var available = (p.variants || []).some(function (v) { return v.available; });
    var firstAvail = (p.variants || []).find(function (v) { return v.available; });
    var img = (p.images && p.images[0] && p.images[0].src) || '';
    var hoverImg = (p.images && p.images[1] && p.images[1].src) || '';
    var createdAt = p.created_at ? Math.floor(new Date(p.created_at).getTime() / 1000) : 0;
    var variantsCount = (p.variants || []).length;

    var article = document.createElement('article');
    article.className = 'nds-cm__card';
    article.setAttribute('data-cm-card', '');
    article.setAttribute('data-product-id', p.id);
    article.setAttribute('data-handle', p.handle);
    article.setAttribute('data-available', available ? 'true' : 'false');
    article.setAttribute('data-price', priceCents);
    article.setAttribute('data-compare-at-price', compareCents);
    article.setAttribute('data-created', createdAt);
    article.setAttribute('data-title', (p.title || '').toLowerCase());
    article.setAttribute('data-manual-index', idx);
    article.setAttribute('data-variants-count', variantsCount);
    if (firstAvail) article.setAttribute('data-quickadd-variant', firstAvail.id);

    var url = '/products/' + p.handle;
    var priceFmt = formatMoney(priceCents);
    var compareFmt = compareCents > priceCents ? formatMoney(compareCents) : '';
    var quickAddOptionsLabel = active.meta && active.meta.quickAddOptionsLabel ? active.meta.quickAddOptionsLabel : 'Options';
    var quickAddAddLabel = active.meta && active.meta.quickAddAddLabel ? active.meta.quickAddAddLabel : 'Add';

    article.innerHTML =
      '<a class="nds-cm__card-img-wrap" href="' + url + '" aria-label="' + esc(p.title) + '">' +
        (img ? '<img class="nds-cm__card-img" src="' + esc(img) + '" alt="' + esc(p.title) + '" loading="lazy" decoding="async">' : '') +
        (hoverImg ? '<img class="nds-cm__card-img nds-cm__card-img--hover" src="' + esc(hoverImg) + '" alt="" loading="lazy" decoding="async" aria-hidden="true">' : '') +
        (!available
          ? '<span class="nds-cm__card-ribbon nds-cm__card-ribbon--soldout">Sold out</span>'
          : (compareCents > priceCents ? '<span class="nds-cm__card-ribbon">Sale</span>' : '')) +
        (available
          ? '<button type="button" class="nds-cm__card-quickadd" data-cm-quickadd' +
            (firstAvail ? ' data-variant-id="' + firstAvail.id + '"' : '') +
            ' aria-label="Add ' + esc(p.title) + ' to cart">' +
              ICONS.plus +
              '<span>' + esc(variantsCount > 1 ? quickAddOptionsLabel : quickAddAddLabel) + '</span>' +
            '</button>'
          : '') +
      '</a>' +
      '<div class="nds-cm__card-info">' +
        '<a class="nds-cm__card-title" href="' + url + '">' + esc(p.title) + '</a>' +
        '<span class="nds-cm__card-price">' +
          (compareFmt ? '<span class="nds-cm__card-price-compare">' + compareFmt + '</span>' : '') +
          '<span class="nds-cm__card-price-current">' + priceFmt + '</span>' +
        '</span>' +
      '</div>';
    return article;
  }

  // ── QUICK ADD ────────────────────────────────────────────────────
  function handleQuickAdd(btn) {
    var card = btn.closest('[data-cm-card]');
    if (!card) return;
    var variantId = btn.dataset.variantId || card.dataset.quickaddVariant;
    var variantsCount = parseInt(card.dataset.variantsCount, 10) || 1;
    var handle = card.dataset.handle;

    if (variantsCount > 1) {
      if (!handle) { window.location.href = '/products/' + (handle || ''); return; }
      openVariantPicker(handle, btn);
      return;
    }
    if (!variantId) { window.location.href = '/products/' + (handle || ''); return; }
    addVariantToCart(parseInt(variantId, 10), btn);
  }

  function addVariantToCart(variantId, btn) {
    if (btn) btn.setAttribute('data-loading', 'true');
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
    })
      .then(function (r) { if (!r.ok) return r.json().then(function (e) { throw e; }); return r.json(); })
      .then(function () {
        if (btn) btn.setAttribute('data-success', 'true');
        showToast('Added to cart');
        document.dispatchEvent(new CustomEvent('nds:cart:updated'));
      })
      .catch(function (err) {
        console.warn('[NDS CM] add-to-cart failed:', err);
        showToast((err && err.description) || 'Could not add to cart', true);
        throw err;
      })
      .finally(function () {
        if (!btn) return;
        setTimeout(function () {
          btn.removeAttribute('data-loading');
          btn.removeAttribute('data-success');
        }, 1400);
      });
  }

  // ── VARIANT PICKER ───────────────────────────────────────────────

  // Position the picker adjacent to its trigger button on desktop.
  // On mobile the bottom-sheet CSS handles layout, so we clear any
  // desktop inline anchoring and bail. On desktop we compute a spot
  // next to the button (prefer below; flip above if no room),
  // horizontally centered on the button and clamped inside the
  // dialog. Inline top/left drive the position; the `is-anchored`
  // class tells the stylesheet to drop the centering transform so
  // those inline values take effect, while preserving the slide-in
  // animation via a simple translateY transition.
  function positionVariantPicker(triggerBtn) {
    if (!vpPanel) return;
    var isDesktop = window.matchMedia('(min-width: 750px)').matches;

    if (!isDesktop || !triggerBtn || !dialog) {
      vpPanel.classList.remove('is-anchored');
      vpPanel.style.top = '';
      vpPanel.style.left = '';
      return;
    }

    var dialogRect = dialog.getBoundingClientRect();
    var btnRect = triggerBtn.getBoundingClientRect();
    var pickerRect = vpPanel.getBoundingClientRect();

    // Picker width: measured if non-zero, else max-width estimate.
    // Height: measured; fall back to 480 estimate if picker is still
    // in its initial "Loading…" state and hasn't grown to final size.
    var pickerWidth = pickerRect.width || 400;
    var pickerHeight = Math.max(pickerRect.height || 0, 300);

    var gap = 8;
    var edge = 16;

    // Vertical: try placing the picker below the trigger.
    var top = (btnRect.bottom - dialogRect.top) + gap;
    var fitsBelow = top + pickerHeight + edge <= dialogRect.height;
    if (!fitsBelow) {
      // Flip above.
      top = (btnRect.top - dialogRect.top) - pickerHeight - gap;
    }
    // Clamp vertically so the picker never leaves the dialog.
    if (top < edge) top = edge;
    if (top + pickerHeight + edge > dialogRect.height) {
      top = Math.max(edge, dialogRect.height - edge - pickerHeight);
    }

    // Horizontal: center picker on the button's horizontal center,
    // then clamp to [edge, dialog.width - picker.width - edge].
    var btnCenterX = btnRect.left - dialogRect.left + btnRect.width / 2;
    var left = btnCenterX - pickerWidth / 2;
    if (left < edge) left = edge;
    if (left + pickerWidth + edge > dialogRect.width) {
      left = Math.max(edge, dialogRect.width - edge - pickerWidth);
    }

    vpPanel.classList.add('is-anchored');
    vpPanel.style.top = top + 'px';
    vpPanel.style.left = left + 'px';
  }

  function openVariantPicker(handle, triggerBtn) {
    if (submenuOpen) closeSubmenu();
    variantPicker.open = true;
    variantPicker.handle = handle;
    variantPicker.selected = {};
    variantPicker.triggerBtn = triggerBtn || null;

    vpPanel.setAttribute('data-open', 'true');
    if (dialog) dialog.classList.add('has-vp-open');
    // Anchor the picker next to the trigger (desktop); mobile falls
    // through to the bottom-sheet CSS. Done before the opening
    // transition plays so the slide-in starts from the right spot.
    positionVariantPicker(triggerBtn);
    var titleEl = vpPanel.querySelector('[data-cm-vp-title]');
    var priceEl = vpPanel.querySelector('[data-cm-vp-price]');
    var bodyElVP = vpPanel.querySelector('[data-cm-vp-body]');
    var loadEl  = vpPanel.querySelector('[data-cm-vp-loading]');
    var errorEl = vpPanel.querySelector('[data-cm-vp-error]');
    var addBtn  = vpPanel.querySelector('[data-cm-vp-add]');
    var pdpLinks = vpPanel.querySelectorAll('[data-cm-vp-pdp-link]');

    if (titleEl) titleEl.textContent = 'Loading…';
    if (priceEl) priceEl.textContent = '';
    if (bodyElVP) { bodyElVP.hidden = true; bodyElVP.innerHTML = ''; }
    if (errorEl) errorEl.hidden = true;
    if (loadEl)  loadEl.hidden = false;
    if (addBtn)  { addBtn.disabled = true; addBtn.removeAttribute('data-loading'); }
    for (var i = 0; i < pdpLinks.length; i++) pdpLinks[i].href = '/products/' + handle;

    setTimeout(function () {
      var cancel = vpPanel.querySelector('[data-cm-vp-cancel]');
      if (cancel) { try { cancel.focus(); } catch (e) {} }
    }, 60);

    var cached = active.productCache[handle];
    if (cached) { renderVariantPicker(cached); return; }

    fetch('/products/' + encodeURIComponent(handle) + '.js', {
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' }
    })
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then(function (product) {
        active.productCache[handle] = product;
        if (variantPicker.open && variantPicker.handle === handle) {
          renderVariantPicker(product);
        }
      })
      .catch(function (err) {
        console.warn('[NDS CM] variant picker fetch failed:', err);
        if (loadEl) loadEl.hidden = true;
        if (errorEl) errorEl.hidden = false;
        if (titleEl) titleEl.textContent = 'Unable to load options';
      });
  }

  function closeVariantPicker() {
    vpPanel.setAttribute('data-open', 'false');
    if (dialog) dialog.classList.remove('has-vp-open');
    // Intentionally DO NOT tear down the is-anchored class or clear
    // inline top/left here. Doing so synchronously would snap the
    // picker from its anchored position to the centered-fallback
    // position mid-transition (opacity + transform still animating
    // 200ms), producing a visible "flash through center" before the
    // fade completes. The picker is invisible once the transition
    // finishes, so leaving stale inline coords in place is harmless;
    // positionVariantPicker owns the anchoring state lifecycle and
    // will overwrite (desktop reopen) or clear (mobile reopen) on
    // the next open call. The resize listener also re-runs
    // positioning while the picker is open, so live viewport changes
    // are handled without needing a close-time reset.
    var trigger = variantPicker.triggerBtn;
    variantPicker.open = false;
    variantPicker.handle = null;
    variantPicker.product = null;
    variantPicker.selected = {};
    variantPicker.triggerBtn = null;
    if (trigger && trigger.focus) { try { trigger.focus(); } catch (e) {} }
  }

  function renderVariantPicker(product) {
    variantPicker.product = product;
    var firstAvail = (product.variants || []).find(function (v) { return v.available; })
                  || (product.variants || [])[0];
    variantPicker.selected = {};
    if (firstAvail && product.options) {
      for (var i = 0; i < product.options.length; i++) {
        var opt = product.options[i];
        var name = typeof opt === 'string' ? opt : opt.name;
        variantPicker.selected[name] = firstAvail['option' + (i + 1)];
      }
    }

    var titleEl = vpPanel.querySelector('[data-cm-vp-title]');
    var bodyElVP = vpPanel.querySelector('[data-cm-vp-body]');
    var loadEl   = vpPanel.querySelector('[data-cm-vp-loading]');
    var errorEl  = vpPanel.querySelector('[data-cm-vp-error]');
    var pdpLinks = vpPanel.querySelectorAll('[data-cm-vp-pdp-link]');

    if (titleEl) titleEl.textContent = product.title || '';
    if (loadEl) loadEl.hidden = true;
    if (errorEl) errorEl.hidden = true;
    for (var pl = 0; pl < pdpLinks.length; pl++) pdpLinks[pl].href = '/products/' + product.handle;

    if (bodyElVP) {
      bodyElVP.innerHTML = '';
      var options = product.options || [];
      for (var oi = 0; oi < options.length; oi++) {
        var o = options[oi];
        var optName = typeof o === 'string' ? o : o.name;
        var seen = Object.create(null);
        var values = [];
        for (var v = 0; v < product.variants.length; v++) {
          var val = product.variants[v]['option' + (oi + 1)];
          if (val != null && !seen[val]) { seen[val] = true; values.push(val); }
        }
        var group = document.createElement('fieldset');
        group.className = 'nds-cm__vp-group';
        var legend = document.createElement('legend');
        legend.className = 'nds-cm__vp-legend';
        legend.textContent = 'Option';
        group.appendChild(legend);
        var list = document.createElement('div');
        list.className = 'nds-cm__vp-values';
        for (var vi = 0; vi < values.length; vi++) {
          var btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'nds-cm__vp-value';
          btn.setAttribute('data-cm-vp-select', '');
          btn.setAttribute('data-cm-vp-option', optName);
          btn.setAttribute('data-cm-vp-value', values[vi]);
          btn.textContent = values[vi];
          list.appendChild(btn);
        }
        group.appendChild(list);
        bodyElVP.appendChild(group);
      }
      bodyElVP.hidden = false;
    }

    updateVariantPickerUI();
    // Content is now populated; re-anchor with true measured size so
    // tall pickers (many option groups) still clamp inside the dialog.
    positionVariantPicker(variantPicker.triggerBtn);
  }

  function updateVariantPickerUI() {
    var product = variantPicker.product;
    if (!product) return;
    var selected = variantPicker.selected;
    var options = product.options || [];

    // 1. Highlight selected buttons
    var allBtns = vpPanel.querySelectorAll('[data-cm-vp-select]');
    for (var i = 0; i < allBtns.length; i++) {
      var b = allBtns[i];
      var optName = b.getAttribute('data-cm-vp-option');
      var optValue = b.getAttribute('data-cm-vp-value');
      b.setAttribute('aria-pressed', selected[optName] === optValue ? 'true' : 'false');
    }

    // 2. Mark unavailable combos
    for (var gi = 0; gi < options.length; gi++) {
      var optionName = typeof options[gi] === 'string' ? options[gi] : options[gi].name;
      var groupButtons = vpPanel.querySelectorAll('[data-cm-vp-option="' + cssEscape(optionName) + '"]');
      for (var gb = 0; gb < groupButtons.length; gb++) {
        var gbtn = groupButtons[gb];
        var candidate = gbtn.getAttribute('data-cm-vp-value');
        var hasMatch = false;
        for (var vi2 = 0; vi2 < product.variants.length; vi2++) {
          var variant = product.variants[vi2];
          var ok = true;
          for (var oj = 0; oj < options.length; oj++) {
            var name = typeof options[oj] === 'string' ? options[oj] : options[oj].name;
            var want = (name === optionName) ? candidate : selected[name];
            if (want != null && variant['option' + (oj + 1)] !== want) { ok = false; break; }
          }
          if (ok && variant.available) { hasMatch = true; break; }
        }
        if (hasMatch) gbtn.removeAttribute('data-unavailable');
        else gbtn.setAttribute('data-unavailable', 'true');
        gbtn.disabled = false;
      }
    }

    // 3. Find matched variant
    var match = null;
    for (var mi = 0; mi < product.variants.length; mi++) {
      var mv = product.variants[mi];
      var allMatch = true;
      for (var mj = 0; mj < options.length; mj++) {
        var mn = typeof options[mj] === 'string' ? options[mj] : options[mj].name;
        if (selected[mn] !== mv['option' + (mj + 1)]) { allMatch = false; break; }
      }
      if (allMatch) { match = mv; break; }
    }

    // 4. Update price + add button
    var priceEl = vpPanel.querySelector('[data-cm-vp-price]');
    var compareEl = vpPanel.querySelector('[data-cm-vp-compare]');
    var addBtn = vpPanel.querySelector('[data-cm-vp-add]');

    var matchCents = match ? variantPriceCents(match.price) : 0;
    var compareCents = match && match.compare_at_price ? variantPriceCents(match.compare_at_price) : 0;
    if (priceEl) priceEl.textContent = match ? formatMoney(matchCents) : '';
    if (compareEl) {
      if (compareCents > matchCents) {
        compareEl.textContent = formatMoney(compareCents);
        compareEl.hidden = false;
      } else {
        compareEl.hidden = true;
      }
    }
    if (addBtn) {
      var lbl = addBtn.querySelector('[data-cm-vp-add-label]');
      if (match && match.available) {
        addBtn.disabled = false;
        addBtn.setAttribute('data-variant-id', String(match.id));
        if (lbl) lbl.textContent = 'Add to cart';
      } else {
        addBtn.disabled = true;
        addBtn.removeAttribute('data-variant-id');
        if (lbl) lbl.textContent = match ? 'Sold out' : 'Select options';
      }
    }
  }

  // ── TOAST ────────────────────────────────────────────────────────
  var toastEl = null, toastTimer = null;
  function showToast(msg, isError) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'nds-cm-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastEl);
    }
    toastEl.innerHTML = ICONS.check + '<span>' + esc(msg) + '</span>';
    toastEl.style.background = isError ? '#B42318' : '';
    toastEl.style.color = isError ? '#FFF' : '';
    toastEl.setAttribute('data-visible', 'true');
    if (toastTimer) clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { toastEl.setAttribute('data-visible', 'false'); }, 2200);
  }

  // ── OPEN / SWAP / CLOSE ──────────────────────────────────────────
  function openSource(idOrHandle, opts) {
    opts = opts || {};
    var src = findSource(idOrHandle);
    if (!src) return;
    var sid = src.getAttribute('data-nds-cm-source');
    var source = opts.source || 'page';
    var railEntry = source === 'drawer' && isMobileView();

    var isOpen = shell.classList.contains('is-active');
    if (isOpen && sid === active.id) return; // re-open of same source is a no-op

    if (isOpen) {
      // ── SWAP: grid fades out, content swaps, cards cascade back in ──
      // shell / backdrop / header / toolbar all stay perfectly still.
      // Hide any open mega-menu immediately — mirrors the fresh-open
      // behavior so clicking a mega-card always feels like a clean
      // handoff from the header overlay to the modal.
      hideHeaderOverlays();
      // Any open variant picker is tied to the OLD collection's product;
      // drop it so the swap doesn't leave a stale picker visible.
      if (variantPicker.open) closeVariantPicker();
      if (submenuOpen) closeSubmenu();

      if (swapStaggerTimer) { clearTimeout(swapStaggerTimer); swapStaggerTimer = null; }
      grid.classList.remove('is-swap-in'); // clear any lingering cascade
      grid.classList.add('is-swapping');
      setTimeout(function () {
        applySource(src);
        grid.classList.remove('is-swapping');
        // Force reflow so the animation on freshly-inserted cards restarts
        // cleanly even if the class transition happens in the same frame.
        // eslint-disable-next-line no-unused-expressions
        void grid.offsetWidth;
        grid.classList.add('is-swap-in');
        swapStaggerTimer = setTimeout(function () {
          grid.classList.remove('is-swap-in');
          swapStaggerTimer = null;
        }, SWAP_STAGGER_MS);
      }, SWAP_FADE_MS);
      if (!opts.fromUrl) urlSet(sid, true); // replaceState (not a new history entry)
      // Focus close button so keyboard flow stays consistent.
      setTimeout(function () {
        if (closeBtn) { try { closeBtn.focus({ preventScroll: true }); } catch (e) {} }
      }, SWAP_FADE_MS + 40);
      try { document.dispatchEvent(new CustomEvent('nds:modal:swap', { detail: { modalId: sid } })); } catch (e) {}
    } else {
      // ── FRESH OPEN ──
      active.entrySource = source;
      stopAllAnimsCm();
      shell.classList.toggle('nds-cm--drawer-rail', railEntry);
      if (animEnabled) {
        shell.style.opacity = railEntry ? '1' : '0';
        shell.style.transform = railEntry ? 'translateX(100%)' : 'translateY(10px) scale(0.985)';
      }
      if (!railEntry) lockBody();
      hideHeaderOverlays();
      applySource(src);
      shell.classList.add('is-active');
      shell.setAttribute('aria-hidden', 'false');
      lockPageInert();
      try {
        document.dispatchEvent(new CustomEvent('nds:modal:open', {
          detail: { modalId: sid, source: railEntry ? 'drawer-rail' : source }
        }));
      } catch (e) {}
      if (animEnabled) animateShellOpen({ rail: railEntry });
      if (!opts.fromUrl) urlSet(sid, false); // pushState (bookmarkable)
      setTimeout(function () {
        var focusTarget = (isMobileView() && backBtn) ? backBtn : closeBtn;
        if (focusTarget) { try { focusTarget.focus({ preventScroll: true }); } catch (e) {} }
      }, 220);
      // Idle prefetch of next page
      if (active.handle) prefetchSilent(active.handle, 2);
    }
  }

  function closeModal(opts) {
    opts = opts || {};
    if (!shell.classList.contains('is-active')) return;
    if (variantPicker.open) closeVariantPicker();
    if (submenuOpen) closeSubmenu();

    var wasRailEntry = active.entrySource === 'drawer' && isMobileView();
    var railBack = wasRailEntry && !opts.silent;

    if (railBack && animEnabled && !opts.skipAnim) {
      try {
        document.dispatchEvent(new CustomEvent('nds:modal:close', {
          detail: { source: 'collection-modal', target: 'drawer-rail' }
        }));
      } catch (e) {}

      animateShellClose({ rail: true }).then(function () {
        closeModal({ silent: true, skipAnim: true, fromUrl: opts.fromUrl });
      });
      return;
    }

    if (!wasRailEntry && !opts.silent && animEnabled && !opts.skipAnim) {
      try {
        document.dispatchEvent(new CustomEvent('nds:modal:close', {
          detail: { source: 'collection-modal', target: 'page' }
        }));
      } catch (e) {}

      animateShellClose({ rail: false }).then(function () {
        closeModal({ silent: true, skipAnim: true, fromUrl: opts.fromUrl });
      });
      return;
    }

    shell.classList.remove('is-active');
    shell.setAttribute('aria-hidden', 'true');
    // `silent:true` is used when another modal-class UI (currently the
    // mobile drawer) is taking over the screen. The caller has already
    // dispatched nds:modal:open on our behalf, flipping the header's
    // data-modal-open flag on — firing nds:modal:close here would
    // immediately flip it back off and the header would hide on the
    // next scroll. Skip the dispatch; let the new UI own the flag.
    if (!opts.silent) {
      try {
        document.dispatchEvent(new CustomEvent('nds:modal:close', {
          detail: { source: 'collection-modal', target: railBack ? 'drawer-rail' : 'page' }
        }));
      } catch (e) {}
    }
    unlockPageInert();
    if (!wasRailEntry) unlockBody();
    restoreHeaderOverlays();
    if (!opts.fromUrl) urlSet(null, true);
    shell.classList.remove('nds-cm--drawer-rail');
    shell.style.opacity = '';
    shell.style.transform = '';
    shell.style.willChange = '';

    active.id = null;
    active.handle = null;
    active.meta = null;
    active.cardData = [];
    active.state = null;
    active.entrySource = null;
  }

  // ── EVENT WIRING ─────────────────────────────────────────────────
  // Split into two: shell-local handlers (re-attached on every init, since
  // the shell DOM is recreated by the Shopify theme editor on section
  // reload) and global handlers on document/window (wired once).

  function wireShellEvents() {
    // Close button + backdrop
    closeBtn.addEventListener('click', function (e) { e.preventDefault(); closeModal(); });
    if (backBtn) {
      backBtn.addEventListener('click', function (e) { e.preventDefault(); closeModal(); });
    }
    backdropEl.addEventListener('click', function () { closeModal(); });

    // Delegated clicks inside the shell (pills, sort, filters, cards, VP)
    shell.addEventListener('click', function (e) {
      // 1. Pill toggle
      var pill = e.target.closest('[data-cm-pill]');
      if (pill && shell.contains(pill)) {
        e.preventDefault();
        e.stopPropagation();
        toggleSubmenu(pill.getAttribute('data-cm-pill'));
        return;
      }

      // 2. Sort option
      var sortOpt = e.target.closest('[data-cm-sort]');
      if (sortOpt) {
        e.preventDefault();
        var val = sortOpt.getAttribute('data-cm-sort');
        active.state.sort = val;
        var allSort = shell.querySelectorAll('[data-cm-sort]');
        for (var i = 0; i < allSort.length; i++) {
          allSort[i].setAttribute('aria-checked', allSort[i] === sortOpt ? 'true' : 'false');
        }
        var labelEl = shell.querySelector('[data-cm-sort-label]');
        if (labelEl) labelEl.textContent = sortOpt.textContent.trim();
        applyFiltersAndSort();
        closeSubmenu();
        return;
      }

      // 3. Price apply
      if (e.target.closest('[data-cm-price-apply]')) {
        e.preventDefault();
        var minV = priceMinInput && priceMinInput.value ? parseFloat(priceMinInput.value) : null;
        var maxV = priceMaxInput && priceMaxInput.value ? parseFloat(priceMaxInput.value) : null;
        active.state.priceMin = (minV != null && !isNaN(minV)) ? Math.round(minV * 100) : null;
        active.state.priceMax = (maxV != null && !isNaN(maxV)) ? Math.round(maxV * 100) : null;
        applyFiltersAndSort();
        updatePillBadges();
        closeSubmenu();
        return;
      }

      // 4. Price clear
      if (e.target.closest('[data-cm-price-clear]')) {
        e.preventDefault();
        if (priceMinInput) priceMinInput.value = '';
        if (priceMaxInput) priceMaxInput.value = '';
        active.state.priceMin = null;
        active.state.priceMax = null;
        applyFiltersAndSort();
        updatePillBadges();
        return;
      }

      // 5. Quick-add button (inside a card)
      var qa = e.target.closest('[data-cm-quickadd]');
      if (qa && grid.contains(qa)) {
        e.preventDefault();
        e.stopPropagation();
        handleQuickAdd(qa);
        return;
      }

      // 6. Variant picker interactions (contained within vpPanel)
      if (vpPanel.contains(e.target)) {
        var sel = e.target.closest('[data-cm-vp-select]');
        if (sel) {
          e.preventDefault();
          var name = sel.getAttribute('data-cm-vp-option');
          var value = sel.getAttribute('data-cm-vp-value');
          variantPicker.selected[name] = value;
          updateVariantPickerUI();
          return;
        }
        if (e.target.closest('[data-cm-vp-cancel]')) {
          e.preventDefault();
          closeVariantPicker();
          return;
        }
        var addP = e.target.closest('[data-cm-vp-add]');
        if (addP && !addP.disabled) {
          e.preventDefault();
          var vid = parseInt(addP.getAttribute('data-variant-id'), 10);
          if (!vid) return;
          addVariantToCart(vid, addP)
            .then(function () { setTimeout(closeVariantPicker, 700); })
            .catch(function () {});
          return;
        }
      }

      // 6.5. Outside-click → close variant picker only (not the outer modal)
      // Nested-modal best practice: clicks anywhere inside the dialog that
      // aren't on the picker itself or on a quickadd trigger dismiss the
      // picker. The backdrop click listener still closes the whole modal.
      if (variantPicker.open
          && !vpPanel.contains(e.target)
          && !e.target.closest('[data-cm-quickadd]')) {
        closeVariantPicker();
        return;
      }

      // 7. Click outside pill+submenu inside dialog → close submenu
      if (submenuOpen) {
        var sm = shell.querySelector('[data-cm-submenu="' + submenuOpen + '"]');
        var pl = shell.querySelector('[data-cm-pill="' + submenuOpen + '"]');
        if (sm && !sm.contains(e.target) && pl && !pl.contains(e.target)) {
          closeSubmenu();
        }
      }
    });

    // Availability checkbox
    if (availCheckbox) {
      availCheckbox.addEventListener('change', function () {
        active.state.availability = availCheckbox.checked;
        applyFiltersAndSort();
        updatePillBadges();
      });
    }

    // Load more
    loadMoreBtn.addEventListener('click', function () { loadMore(); });
  }

  function wireGlobalEvents() {
    // Global delegated triggers — any element with [data-nds-cm-open] opens
    // the matching source. Re-using the trigger while the shell is open
    // swaps content in-place (single-shell architecture).
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-nds-cm-open]');
      if (!trigger) return;
      var id = trigger.getAttribute('data-nds-cm-open');
      if (!id || !findSource(id)) return;
      e.preventDefault();
      e.stopPropagation();
      var fromDrawer = !!trigger.closest('.nds-drawer[data-open="true"]');
      openSource(id, { source: fromDrawer ? 'drawer' : 'page' });
    }, { capture: true });

    // When the mobile drawer opens while a modal is active, close the
    // modal first. Without this the drawer paints over the modal and a
    // subsequent card-tap inside the drawer hits openSource's SWAP
    // path (since is-active is still true) — swapping collection
    // content invisibly under the drawer.
    //
    // Close silently: the drawer has already fired its own
    // nds:modal:open and owns the header's data-modal-open flag now.
    // A paired nds:modal:close from us would flip the header off and
    // hide it on the next scroll.
    document.addEventListener('nds:modal:open', function (e) {
      var detail = e.detail || {};
      if (detail.source !== 'drawer') return;
      if (shell && shell.classList.contains('is-active')) {
        closeModal({ silent: true });
      }
    });

    document.addEventListener('nds:modal:close', function (e) {
      var detail = e.detail || {};
      if (detail.source !== 'drawer') return;
      if (!shell || !shell.classList.contains('is-active')) return;
      if (active.entrySource === 'drawer') closeModal({ silent: true, skipAnim: true });
    });

    // ESC dismisses the deepest open layer first
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      if (variantPicker.open) { closeVariantPicker(); return; }
      if (submenuOpen) { closeSubmenu(); return; }
      if (shell && shell.classList.contains('is-active')) closeModal();
    });

    // Hover / touch prefetch of collection page 2 for faster load-more
    var hoverTimers = new WeakMap();
    var HOVER_MS = 150;
    function schedulePrefetch(trigger) {
      var id = trigger.getAttribute('data-nds-cm-open');
      if (!id) return;
      var src = findSource(id);
      if (!src) return;
      var h = src.getAttribute('data-handle');
      if (h) prefetchSilent(h, 2);
    }
    document.addEventListener('mouseenter', function (e) {
      if (!(e.target instanceof Element)) return;
      var trigger = e.target.closest && e.target.closest('[data-nds-cm-open]');
      if (!trigger) return;
      if (hoverTimers.has(trigger)) return;
      var t = setTimeout(function () { schedulePrefetch(trigger); }, HOVER_MS);
      hoverTimers.set(trigger, t);
    }, true);
    document.addEventListener('mouseleave', function (e) {
      if (!(e.target instanceof Element)) return;
      var trigger = e.target.closest && e.target.closest('[data-nds-cm-open]');
      if (!trigger) return;
      var t = hoverTimers.get(trigger);
      if (t) { clearTimeout(t); hoverTimers.delete(trigger); }
    }, true);
    document.addEventListener('touchstart', function (e) {
      if (!(e.target instanceof Element)) return;
      var trigger = e.target.closest && e.target.closest('[data-nds-cm-open]');
      if (trigger) schedulePrefetch(trigger);
    }, { capture: true, passive: true });

    // Re-anchor the variant picker on window resize so it stays pinned
    // to its trigger through browser resize, device rotation, and
    // desktop↔mobile breakpoint crossings. rAF-throttled to avoid
    // thrashing getBoundingClientRect during a continuous resize drag.
    var resizeRaf = 0;
    window.addEventListener('resize', function () {
      if (!variantPicker.open && !submenuOpen) return;
      if (resizeRaf) return;
      resizeRaf = requestAnimationFrame(function () {
        resizeRaf = 0;
        if (variantPicker.open) positionVariantPicker(variantPicker.triggerBtn);
        if (submenuOpen && shell) {
          var menu = shell.querySelector('[data-cm-submenu="' + submenuOpen + '"]');
          var pill = shell.querySelector('[data-cm-pill="' + submenuOpen + '"]');
          if (menu && pill) positionSubmenu(menu, pill);
        }
      });
    });

    // Popstate — react to browser back/forward navigating the ?cm=... param
    window.addEventListener('popstate', function () {
      var wanted = urlCurrent();
      if (!wanted) {
        if (shell && shell.classList.contains('is-active')) closeModal({ fromUrl: true });
        return;
      }
      if (findSource(wanted)) openSource(wanted, { fromUrl: true });
    });
  }

  // ── INITIAL URL → OPEN (for direct links / refresh) ──────────────
  function applyInitialUrl() {
    var wanted = urlCurrent();
    if (!wanted) return;
    if (!findSource(wanted)) {
      // Unknown handle/id on load → drop it silently
      urlSet(null, true);
      return;
    }
    openSource(wanted, { fromUrl: true });
  }

  // ── IDLE PREFETCH OF FIRST SOURCE'S NEXT PAGE ────────────────────
  function scheduleIdlePrefetch() {
    var ids = Object.keys(sourceById);
    if (ids.length === 0) return;
    var first = sourceById[ids[0]];
    var h = first && first.getAttribute('data-handle');
    if (!h) return;
    var run = function () { prefetchSilent(h, 2); };
    if ('requestIdleCallback' in window) window.requestIdleCallback(run, { timeout: 3000 });
    else setTimeout(run, 1500);
  }

  // ── BOOTSTRAP ────────────────────────────────────────────────────
  var globalsWired = !!window.__ndsCollectionModalGlobalsWired;

  function init() {
    shell = document.querySelector('[data-nds-cm-shell]');
    if (!shell) return; // No collection_modal blocks on this page

    // Pull the store's money_format (e.g. "{{amount}} NIS") so prices
    // rendered by JS match the format used by Liquid's {{ x | money }}
    // filter in the SSR cards.
    moneyFormat = shell.getAttribute('data-money-format') || '';

    dialog        = shell.querySelector('[data-cm-dialog]');
    title         = shell.querySelector('[data-cm-title]');
    countNum      = shell.querySelector('[data-cm-count-num]');
    countLabel    = shell.querySelector('[data-cm-count-label]');
    grid          = shell.querySelector('[data-cm-grid]');
    loadMoreWrap  = shell.querySelector('[data-cm-load-more-wrap]');
    loadMoreBtn   = shell.querySelector('[data-cm-load-more]');
    toolbarLeft   = shell.querySelector('[data-cm-toolbar-left]');
    toolbarRight  = shell.querySelector('[data-cm-toolbar-right]');
    availMenu     = shell.querySelector('[data-cm-submenu="availability"]');
    priceMenu     = shell.querySelector('[data-cm-submenu="price"]');
    sortMenu      = shell.querySelector('[data-cm-submenu="sort"]');
    sortList      = shell.querySelector('[data-cm-sort-list]');
    availCheckbox = shell.querySelector('[data-cm-availability]');
    priceMinInput = shell.querySelector('[data-cm-price-min]');
    priceMaxInput = shell.querySelector('[data-cm-price-max]');
    bodyEl        = shell.querySelector('[data-cm-body]');
    backdropEl    = shell.querySelector('[data-cm-backdrop]');
    closeBtn      = shell.querySelector('[data-cm-close]');
    backBtn       = shell.querySelector('[data-cm-back]');
    vpPanel       = shell.querySelector('[data-cm-submenu="variant-picker"]');

    if (animEnabled) shell.classList.add('nds-motion-on');
    else shell.classList.remove('nds-motion-on');

    collectSources();

    // Shell-local handlers must be re-attached when the shell DOM node is
    // replaced (Shopify theme editor reloads the whole header section).
    // Tag the shell with a data attribute so repeated init() calls against
    // the SAME node don't stack duplicate listeners.
    if (!shell.hasAttribute('data-nds-cm-wired')) {
      wireShellEvents();
      shell.setAttribute('data-nds-cm-wired', 'true');
    }
    // Global handlers target document/window which persist across section
    // reloads → attach exactly once for the lifetime of the page.
    if (!globalsWired) {
      wireGlobalEvents();
      globalsWired = true;
      window.__ndsCollectionModalGlobalsWired = true;
    }

    applyInitialUrl();
    scheduleIdlePrefetch();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  // Re-init on theme editor section reload (the shell + templates may
  // have been re-rendered by Shopify with a fresh DOM).
  document.addEventListener('shopify:section:load', function (e) {
    if (!e.target || !e.target.querySelector) return;
    if (e.target.querySelector('[data-nds-cm-shell]') ||
        e.target.querySelector('template[data-nds-cm-source]')) {
      init();
    }
  });
})();
